import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Button, Container, Grid, GridCol, Group, TextInput } from '@mantine/core';
import {
  deleteWithdrawSetDetails,
  IWithdrawSetRequest,
  IWithdrawSets,
  IWithdrawSetsResponse,
  updateWithdrawSetDetails
} from '../../../libraries/brisa';
import { StatusOper } from '../../../libraries/zps';
import { AppState } from '../../client-redux/reducers';
import ModalComponent from '../../components/modal';
import StatusOperModal from '../../components/status-oper-modal';
import { Language } from '../../languages/languageHandler';
import ValuesTable from './withdraw-values-table';
import DisplayField from '../../components/display-field';

interface AddEditwithdrawSetProps {
  withdrawSet?: IWithdrawSets;
  withdrawSetList?: IWithdrawSetsResponse;
  defaultEntid?: number; // only used for new mode
  mode?: 'new' | 'edit';
  opened: boolean;
  close: () => void;
  onSaved?: () => void;
}

const AddEditWithdrawSet = ({
  withdrawSet,
  withdrawSetList,
  defaultEntid,
  mode,
  opened,
  close,
  onSaved
}: AddEditwithdrawSetProps) => {
  const lang = useSelector<AppState, Language>(state => state.lang.lang);
  const controller = new AbortController();

  const [formData, setFormData] = useState<IWithdrawSetRequest>({
    description: '',
    entid: 0,
    id: 0,
    mc: []
  });

  const [statusOper, setStatusOper] = useState<StatusOper | null>(null);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [actionType, setActionType] = useState<'add' | 'edit' | 'delete'>('add');

  useEffect(() => {
    if (mode === 'new') {
      const newId =
        withdrawSetList && withdrawSetList.list.length > 0 ? Math.max(...withdrawSetList.list.map(w => w.id)) + 1 : 1;

      setFormData({
        description: '',
        entid: defaultEntid || 0,
        id: newId,
        mc: []
      });
    } else if (withdrawSet) {
      setFormData({
        description: withdrawSet.description,
        entid: withdrawSet.entid,
        id: withdrawSet.id,
        mc: withdrawSet.mc || []
      });
    }
  }, [withdrawSet, mode, withdrawSetList, defaultEntid]);

  const showStatus = (status: StatusOper) => {
    setStatusOper(status);
    setShowStatusModal(true);

    if (status.code === 0) {
      setTimeout(() => {
        setShowStatusModal(false);
        onSaved?.();
        close();
      }, 2000);
    }
  };

  const handleChange = (field: keyof IWithdrawSetRequest, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    setActionType(mode === 'new' ? 'add' : 'edit');

    try {
      const response = await updateWithdrawSetDetails(formData, controller); // same function works for "add" & "edit"
      showStatus(response.statusOper);
    } catch (err: any) {
      const statusOper: StatusOper = err?.response?.data?.statusOper ?? {
        code: -1,
        message: err.message || 'Error'
      };
      showStatus(statusOper);
    }
  };

  const handleDelete = async () => {
    console.log('Delete clicked!', formData.entid, formData.id);
    setActionType('delete');

    if (!formData.entid || !formData.id) {
      console.warn('Cannot delete: entid or id is missing');
      return;
    }

    try {
      const response = await deleteWithdrawSetDetails(formData.entid, formData.id);
      console.log('Delete API response:', response);
      showStatus(response.statusOper);
    } catch (err: any) {
      console.error('Delete error:', err);
      const statusOper: StatusOper = err?.response?.data?.statusOper ?? {
        code: -1,
        message: err.message || 'Error'
      };
      showStatus(statusOper);
    }
  };

  const messageMap = {
    add: {
      success: lang.BRISA_WITHDRAW_SETS_ADD_SUCCESS,
      error: lang.BRISA_WITHDRAW_SETS_ADD_ERROR
    },
    edit: {
      success: lang.BRISA_WITHDRAW_SETS_EDIT_SUCCESS,
      error: lang.BRISA_WITHDRAW_SETS_EDIT_ERROR
    },
    delete: {
      success: lang.BRISA_WITHDRAW_SETS_DELETE_SUCCESS,
      error: lang.BRISA_WITHDRAW_SETS_DELETE_ERROR
    }
  };

  const message = messageMap[actionType];
  const title =
    mode === 'new' ? `${lang.BRISA_WITHDRAW_SETS_ADD}` : `${lang.BRISA_WITHDRAW_SETS_EDIT} - ${withdrawSet?.id}`;

  return (
    <>
      <ModalComponent
        show={opened}
        handleClick={close}
        title={title}
        content={
          <Container className="withdraw-set-modal">
            <Grid className="withdraw-details">
              <GridCol span={{ base: 12, xs: 6, sm: 6, md: 4 }}>
                <DisplayField title={lang.BRISA_WITHDRAW_SETS_ENTITY} content={formData.entid} />
              </GridCol>

              <GridCol span={{ base: 12, xs: 6, sm: 6, md: 4 }}>
                <DisplayField title={lang.BRISA_WITHDRAW_SETS_ID} content={formData.id} />
              </GridCol>

              <GridCol span={{ base: 12, xs: 8, sm: 8, md: 8 }}>
                <TextInput
                  label={lang.BRISA_WITHDRAW_SETS_DESCRIPTION}
                  value={formData.description}
                  onChange={e => handleChange('description', e.currentTarget.value)}
                />
              </GridCol>
              <GridCol className="values-table-container">
                <ValuesTable mc={formData.mc} onChange={newMc => handleChange('mc', newMc)} />
              </GridCol>
            </Grid>

            <Group className="buttons">
              <Button onClick={handleSave}>{lang.GLOBAL_SAVE_BUTTON}</Button>
              <Button onClick={handleDelete}>{lang.GLOBAL_DELETE}</Button>
            </Group>
          </Container>
        }
      />

      {statusOper && (
        <StatusOperModal
          opened={showStatusModal}
          onClose={() => setShowStatusModal(false)}
          statusOper={statusOper}
          successMessage={statusOper.code === 0 ? message?.success : undefined}
          errorMessage={statusOper.code !== 0 ? message?.error : undefined}
        />
      )}
    </>
  );
};

export default AddEditWithdrawSet;
